import React, { useState } from "react";
import WindowChrome from "../shared/WindowChrome.jsx";
import CaseScreenshot from "../shared/CaseScreenshot.jsx";

const LINKEDIN_VERIFY_URL = "https://www.linkedin.com/";
const CASE_CLIENT_NAME = "Oz Exteriors Roofing Group";
const CASE_CLIENT_URL = "https://ozexteriors.com.au/";
const CASE_CLIENT_LOCATION = "Australia";

const SUPPORTING_METRICS = [
  {
    id: "response",
    value: "26 sec",
    label: "response time",
    context: "vs 30–40 min office",
  },
  {
    id: "conversion",
    value: "+76%",
    label: "conversion",
    context: "form → booked",
  },
  {
    id: "revenue",
    value: "$43,200",
    label: "net revenue",
    context: "additional projection",
  },
];

const LOG_ENTRIES = [
  {
    id: "form",
    timestamp: "2026-06-23 22:23:06",
    role: "LEAD",
    message: "Form received · Roof Restoration · Flinders, NSW · AU",
    detail: "Name, phone, email, and street address withheld",
  },
  {
    id: "first-reply",
    timestamp: "2026-06-23 22:23:19",
    role: "FRACTOLABS",
    message: "Jack replied in 13s · Service area confirmed · Owner/tenant check started",
  },
  {
    id: "qualified",
    timestamp: "2026-06-23 22:29:44",
    role: "LEAD",
    message: "Qualified · Owner · Townhouse · Double storey · Concrete tiles",
    detail: "Planning ahead · Roof ~20 years old",
  },
  {
    id: "slot",
    timestamp: "2026-06-23 22:33:12",
    role: "LEAD",
    message: "Morning inspection window selected",
  },
  {
    id: "booked",
    timestamp: "2026-06-23 22:33:33",
    role: "FRACTOLABS",
    message: "Inspection booked · Thu 25 Jun · 09:00–11:00 · SMS reminder queued",
    highlight: true,
  },
];

const TRUST_ITEMS = [
  "Verified deployment",
  `Roofing · ${CASE_CLIENT_LOCATION}`,
  "Owner confirm on LinkedIn",
];

const QuietProofSection = () => {
  const [logOpen, setLogOpen] = useState(false);

  return (
    <section className="cursor-section cursor-proof-section" data-testid="quiet-proof-section">
      <div className="cursor-proof-inner">
        <header className="cursor-proof-section-header">
          <div className="cursor-proof-section-label-row">
            <span className="cursor-proof-badge" data-testid="quiet-proof-eyebrow">
              <span className="cursor-proof-badge-dot" aria-hidden="true" />
              Live customer
            </span>
            <span className="cursor-proof-section-index">02 · Case study</span>
          </div>

          <h2 className="cursor-proof-section-title">Real company. Measured results.</h2>
          <p className="cursor-proof-not-demo">
            Not a sandbox — an actual roofing business using FractoLabs since May 2026.
          </p>

          <div className="cursor-proof-client-block">
            <h3 className="cursor-proof-client">
              <a
                href={CASE_CLIENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-proof-client-link"
              >
                {CASE_CLIENT_NAME}
              </a>
              <span className="cursor-proof-client-location"> · {CASE_CLIENT_LOCATION}</span>
            </h3>
            <p className="cursor-proof-meta">
              Deployed May 2026 · Live in 8 minutes ·{" "}
              <a
                href={CASE_CLIENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-proof-site-link"
              >
                ozexteriors.com.au ↗
              </a>
            </p>

            <ul className="cursor-proof-trust-list" aria-label="Verification details">
              {TRUST_ITEMS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <div className="cursor-proof-header-links">
              <a
                href={CASE_CLIENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="quiet-cta-outline cursor-proof-header-link"
              >
                Visit live website ↗
              </a>
              <a
                href={LINKEDIN_VERIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="quiet-cta-outline cursor-proof-header-link"
                data-testid="quiet-proof-linkedin"
              >
                Verify with owner on LinkedIn ↗
              </a>
            </div>
          </div>
        </header>

        <CaseScreenshot
          windowTitle="live_site · ozexteriors.com.au"
          caption="Their live website · ozexteriors.com.au"
        />

        <div className="cursor-window cursor-proof-window cursor-proof-metrics-window">
          <WindowChrome title="live_deployment · metrics" variant="case" />

          <div className="cursor-proof-body">
            <p className="cursor-proof-window-label">Performance after go-live</p>

            <div className="cursor-proof-hero-metric">
              <p className="cursor-proof-hero-value">+6</p>
              <p className="cursor-proof-hero-label">additional appointments per week</p>
              <p className="cursor-proof-hero-context">Started after digital employee went live</p>
            </div>

            <div className="cursor-proof-metrics-grid">
              {SUPPORTING_METRICS.map((metric) => (
                <div key={metric.id} className="cursor-proof-metric-tile">
                  <p className="cursor-proof-metric-value">{metric.value}</p>
                  <p className="cursor-proof-metric-label">{metric.label}</p>
                  <p className="cursor-proof-metric-context">{metric.context}</p>
                </div>
              ))}
            </div>

            <div className="cursor-proof-actions">
              <button
                type="button"
                className="cursor-proof-log-toggle"
                onClick={() => setLogOpen((open) => !open)}
                aria-expanded={logOpen}
                data-testid="quiet-proof-log-toggle"
              >
                {logOpen ? "▾ hide event log" : "▸ view event log from live deployment"}
              </button>
            </div>

            {logOpen && (
              <div className="cursor-proof-log-panel" data-testid="quiet-proof-log">
                <p className="cursor-proof-log-note">
                  Excerpt from live Oz Exteriors deployment · personal details removed
                </p>
                <div className="cursor-changelog cursor-proof-log">
                  {LOG_ENTRIES.map((entry) => (
                    <div
                      key={entry.id}
                      className={`cursor-log-entry${entry.highlight ? " cursor-log-entry-highlight" : ""}`}
                    >
                      <div className="cursor-log-entry-head">
                        <span className="cursor-log-timestamp">[{entry.timestamp}]</span>
                        <span className={`cursor-log-role cursor-log-role-${entry.role.toLowerCase()}`}>
                          {entry.role}
                        </span>
                      </div>
                      <p className="cursor-log-message">{entry.message}</p>
                      {entry.detail && <p className="cursor-log-detail">{entry.detail}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuietProofSection;
